import { colorsMapping } from '@repo/toolkit/colors'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import DataTable from '@repo/ui/data-table'
import { PharmacyIcon, StarFilledIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import { useQuery } from '@tanstack/react-query'

import { getCenterGroupEnrollments } from '../../../../api/centers/center-groups'
import useTranslate from '../../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { medicalStatusEnumMapping } from '../../../../lib/types/enums/enrollment/medical-status.enum'
import { friendPlaceEnumMapping } from '../../../../lib/types/enums/friend/friend-place.enum'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentCenter } from '../../../../lib/types/models/enrollment-center/enrollment-center'
import PeriodBadge from '../../../elements/period-badge'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = {
  centerId: number
  groupId: number
  className?: string
  onClose?: () => void
}

const CenterGroupEnrollments = ({ centerId, groupId, className, onClose }: Props) => {
  const { t } = useTranslate()

  const { data, isPending } = useQuery({
    queryKey: ['center-group-enrollments', centerId, groupId],
    queryFn: () => getCenterGroupEnrollments(centerId, groupId)
  })

  if (isPending || !data) return <Loader center />

  return (
    <div>
      <div className={className}>
        <DataTable<EnrollmentCenter>
          columns={[
            {
              accessorKey: 'enrollment.mainProposition.journey.reference',
              header: t('center.groups.students.journeys'),
              cell: ({ row }) => {
                const journey = row.original.enrollment.mainProposition?.journey
                if (!journey) return '-'
                return <PeriodBadge period={journey.date.period} text={journey.reference} />
              }
            },

            {
              accessorKey: 'enrollment.enrollmentNumber',
              header: t('center.groups.students.enrollment.number')
            },
            {
              accessorKey: 'enrollment.childLastName',
              header: t('center.groups.students.last.name'),
              cell: ({ row }) => row.original.enrollment.childLastName?.toUpperCase()
            },
            {
              accessorKey: 'enrollment.childFirstName',
              header: t('center.groups.students.first.name'),
              cell: ({ row }) =>
                row.original.enrollment.childFirstName
                  ? StringFormatter.capitalize(row.original.enrollment.childFirstName)
                  : '-'
            },
            {
              accessorKey: 'enrollment.childBirthDate',
              header: t('center.groups.students.age'),
              enableSorting: true,
              invertSorting: true,
              cell: ({ row }) =>
                EnrollmentProperties.getAgeAtDeparture(row.original.enrollment) ?? '-'
            },
            {
              accessorKey: 'enrollment.childGender',
              header: t('center.groups.students.gender'),
              enableSorting: true,
              cell: ({ row }) =>
                row.original.enrollment.childGender
                  ? genderOneLetterMapping[row.original.enrollment.childGender].label
                  : '-'
            },
            {
              accessorKey: 'enrollment.childSchoolLevel',
              header: t('center.groups.students.class'),
              enableSorting: true,
              cell: ({ row }) =>
                row.original.enrollment.childSchoolLevel
                  ? schoolLevelEnumMapping[row.original.enrollment.childSchoolLevel]
                  : '-'
            },
            {
              accessorKey: 'enrollment.isAA',
              header: 'AA',
              enableSorting: true,
              cell: ({ row }) =>
                row.original.enrollment.isAA ? <StarFilledIcon width="24px" height="24px" /> : '-'
            },
            {
              accessorKey: 'enrollment.medicalStatus',
              header: t('center.groups.students.health'),
              enableSorting: true,
              cell: ({ row }) => {
                const status = medicalStatusEnumMapping[row.original.enrollment.medicalStatus]
                return (
                  <PharmacyIcon
                    className="!size-5 shrink-0"
                    color={colorsMapping[status.variant ?? 'grey'].text}
                    color2={colorsMapping[status.variant ?? 'grey'].hover}
                  />
                )
              }
            },
            {
              accessorKey: 'enrollment.blockedFriends',
              header: t('center.groups.students.friends'),
              cell: ({ row }) => (
                <div className="flex gap-2 flex-col my-2">
                  {row.original.enrollment.blockedFriends?.map((blockedFriend) => (
                    <Badge
                      key={blockedFriend.id}
                      variant={blockedFriend.affectedEnrollmentId ? 'blue' : 'grey'}
                      size="small"
                      text={`${blockedFriend.firstName} ${blockedFriend.lastName}${blockedFriend.type ? ` - ${t(friendPlaceEnumMapping[blockedFriend.type].label)}` : ''}`}
                    />
                  ))}
                  {row.original.enrollment.blockedByFriends?.map((blockedByFriend) => (
                    <Badge
                      key={`blocked-by-${blockedByFriend.id}`}
                      variant="blue"
                      size="small"
                      text={`${blockedByFriend.originalEnrollment?.childFirstName ?? ''} ${blockedByFriend.originalEnrollment?.childLastName ?? ''}${blockedByFriend.type ? ` - ${t(friendPlaceEnumMapping[blockedByFriend.type].label)}` : ''}`}
                    />
                  ))}
                </div>
              )
            }
          ]}
          disableRow={(row) => row.center.locked}
          data={data}
          rowCount={data.length}
          paginated={false}
          manualSorting={false}
        />
      </div>

      <ValidationButtons
        hasBorderTop
        buttonsConfig={[
          {
            label: t('center.groups.close'),
            variant: 'ghost',
            bypassValidation: true,
            type: 'button',
            action: () => {
              onClose?.()
            }
          }
        ]}
      />
    </div>
  )
}

export default CenterGroupEnrollments
